import {type XAxisFormatValues} from '../../../../../generated/graphql';
import {getHasSystemMetrics} from '../../../../PanelRunsLinePlot/common';
import {type RunsLinePlotConfig} from '../../../../PanelRunsLinePlot/types';
import {
  getCascadingSetting,
  resolveNullableCascade,
} from '../../getCascadingSetting';
import {type LinePlotSettings, type XAxisValues} from '../../types';
import {isSettingActive} from '../../utils';
import {DEFAULT_X_AXIS_SETTINGS} from '../linePlotDefaults';

export const getCascadingXAxisSettings = (
  parentSettings: LinePlotSettings | undefined,
  childSettings?: LinePlotSettings,
  panelSettings?: RunsLinePlotConfig
): {
  xAxis: XAxisValues | string;
  xAxisMin: number | undefined;
  xAxisMax: number | undefined;
  xLogScale: boolean | undefined;
  xAxisFormat: XAxisFormatValues | undefined;
} => {
  const xAxis = getXAxis(parentSettings, childSettings, panelSettings);

  return {
    xAxis,
    xAxisMin: getXAxisRangeValue(
      'xAxisMin',
      xAxis,
      parentSettings,
      childSettings,
      panelSettings
    ),
    xAxisMax: getXAxisRangeValue(
      'xAxisMax',
      xAxis,
      parentSettings,
      childSettings,
      panelSettings
    ),
    xLogScale: getCascadingSetting([
      parentSettings?.xLogScale,
      childSettings?.xLogScale,
      panelSettings?.xLogScale,
    ]),
    xAxisFormat: getCascadingSetting([
      parentSettings?.xAxisFormat,
      childSettings?.xAxisFormat,
      panelSettings?.xAxisFormat,
    ]),
  };
};

const getXAxis = (
  parentSettings: LinePlotSettings | undefined,
  childSettings?: LinePlotSettings,
  panelSettings?: RunsLinePlotConfig
) => {
  const isSystemMetricPanel =
    panelSettings != null && getHasSystemMetrics(panelSettings);
  const xAxis = getCascadingSetting([
    parentSettings?.xAxis,
    childSettings?.xAxis,
  ]);

  if (panelSettings?.xAxis != null) {
    return panelSettings?.xAxis;
  }

  if (isSystemMetricPanel) {
    // the panel doesn't have its own xAxis, but it can't use the global
    // xAxis because it isn't compatible with system metrics -- fall back
    // to '_runtime'
    if (['_runtime', '_timestamp'].indexOf(xAxis) === -1) {
      return '_runtime';
    }
    return xAxis;
  }

  if (
    panelSettings?.startingXAxis != null &&
    childSettings?.xAxis == null &&
    parentSettings?.xAxis == null
  ) {
    // If the global xAxis settings are not active, use default x-axis.
    // This startingXAxis value can be populated if user calls wandb.define_metric()
    // and specifies the step_metric arg. This arg is used to specify which x-axis should
    // be used for auto-generated charts. Look at sdk code for further details
    // https://github.com/wandb/wandb/blob/fa4a7e4374fd4a0048548a483516f98482f5fdc3/wandb/sdk/wandb_run.py#L2736-L2737
    return panelSettings?.startingXAxis;
  }

  if (childSettings?.xAxis != null) {
    return childSettings?.xAxis;
  }

  return xAxis;
};

const getXAxisRangeValue = (
  field: 'xAxisMin' | 'xAxisMax',
  globalXAxis: XAxisValues | string | undefined,
  parentSettings: LinePlotSettings | undefined,
  childSettings?: LinePlotSettings,
  panelSettings?: RunsLinePlotConfig
): number | undefined => {
  // Don't inherit range bounds from parent levels if the panel uses a
  // different xAxis — the bounds would be meaningless for a different axis.
  const panelHasDivergentXAxis =
    panelSettings != null &&
    isSettingActive(panelSettings.xAxis, DEFAULT_X_AXIS_SETTINGS.xAxis) &&
    globalXAxis !== panelSettings.xAxis;

  if (panelHasDivergentXAxis) {
    return panelSettings?.[field] ?? undefined;
  }

  return resolveNullableCascade(
    parentSettings?.[field],
    childSettings?.[field],
    panelSettings?.[field]
  );
};
